import { useSyncExternalStore } from "react";

export const APP_STYLES = [
  {
    id: "classic",
    name: "Classic",
    tagline: "The current neutral look",
    swatches: ["oklch(0.205 0 0)", "oklch(0.922 0 0)", "oklch(1 0 0)"],
  },
  {
    id: "ayu-mirage",
    name: "Ayu Mirage",
    tagline: "Cool neutrals, golden accents",
    swatches: ["#242936", "#ffcc66", "#1f2430"],
  },
  {
    id: "dusk",
    name: "Dusk",
    tagline: "Plum evening, lantern glow",
    swatches: ["oklch(0.45 0.12 305)", "oklch(0.82 0.12 62)", "oklch(0.2 0.03 300)"],
  },
  {
    id: "lantern",
    name: "Lantern",
    tagline: "Dusk colors, floating rounded islands",
    swatches: ["oklch(0.82 0.12 62)", "oklch(0.45 0.12 305)", "oklch(0.995 0.004 300)"],
  },
  {
    id: "pop",
    name: "Pop",
    tagline: "Pink header, ink outlines, chunky chips",
    swatches: ["oklch(0.56 0.21 358)", "oklch(0.85 0.09 190)", "oklch(0.93 0.1 95)"],
  },
  {
    id: "grove",
    name: "Grove",
    tagline: "Moss greens, manuscript page layout",
    swatches: ["oklch(0.48 0.09 155)", "oklch(0.78 0.11 150)", "oklch(0.935 0.025 150)"],
  },
] as const;

export type AppStyle = (typeof APP_STYLES)[number]["id"];

const STORAGE_KEY = "app-style";

function isAppStyle(value: string | null): value is AppStyle {
  return APP_STYLES.some((style) => style.id === value);
}

function getStoredStyle(): AppStyle {
  if (typeof window === "undefined") return "classic";
  const stored = localStorage.getItem(STORAGE_KEY);
  return isAppStyle(stored) ? stored : "classic";
}

function applyStyle(style: AppStyle) {
  document.documentElement.dataset.style = style;
}

// Called once at startup so the stored style is applied before the first render
export function applyStoredAppStyle() {
  applyStyle(getStoredStyle());
}

// One style for the whole app, so every switcher shows the same one
let current: AppStyle = getStoredStyle();
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function setStyle(newStyle: AppStyle) {
  localStorage.setItem(STORAGE_KEY, newStyle);
  applyStyle(newStyle);
  current = newStyle;
  for (const listener of listeners) listener();
}

export function useAppStyle() {
  const style = useSyncExternalStore(subscribe, () => current);
  return { style, setStyle };
}
